import React, { createContext, useCallback, useContext, useState } from 'react';

export interface ChatHeaderState {
  isActive: boolean;
  chatTitle: string;
  setChatTitle: (title: string) => void;
  chatId: string | null;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  onTitleBlur: (currentTitle: string) => void;
  onSaveClick: () => void;
}

export interface ChatHeaderRegistration {
  chatTitle: string;
  chatId: string | null;
  onTitleBlur: (currentTitle: string) => void;
  onSaveClick: () => void;
}

interface ChatHeaderContextValue extends ChatHeaderState {
  register: (state: ChatHeaderRegistration) => void;
  unregister: () => void;
}

const defaultState: ChatHeaderState = {
  isActive: false,
  chatTitle: '',
  setChatTitle: () => {},
  chatId: null,
  menuOpen: false,
  setMenuOpen: () => {},
  onTitleBlur: () => {},
  onSaveClick: () => {},
};

const ChatHeaderContext = createContext<ChatHeaderContextValue | null>(null);

export function ChatHeaderProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ChatHeaderState>(defaultState);

  const setChatTitle = useCallback((title: string) => {
    setState((prev) => ({ ...prev, chatTitle: title }));
  }, []);

  const setMenuOpen = useCallback((open: boolean) => {
    setState((prev) => ({ ...prev, menuOpen: open }));
  }, []);

  const register = useCallback((s: ChatHeaderRegistration) => {
    setState({
      isActive: true,
      chatTitle: s.chatTitle,
      setChatTitle: () => {},
      chatId: s.chatId,
      menuOpen: false,
      setMenuOpen: () => {},
      onTitleBlur: s.onTitleBlur,
      onSaveClick: s.onSaveClick,
    });
  }, []);

  const unregister = useCallback(() => {
    setState((prev) => ({ ...prev, isActive: false }));
  }, []);

  const value: ChatHeaderContextValue = {
    ...state,
    setChatTitle,
    setMenuOpen,
    register,
    unregister,
  };

  return (
    <ChatHeaderContext.Provider value={value}>
      {children}
    </ChatHeaderContext.Provider>
  );
}

export function useChatHeader() {
  const ctx = useContext(ChatHeaderContext);
  if (!ctx) return null;
  return ctx;
}
